import { useState } from 'react';
import { usePartsCatalog } from '@/hooks/useReferenceData';
import { useAddJobPart } from '@/hooks/useJobParts';
import { usePartsMarkup, useDefaultPartSource, type PartSource } from '@/hooks/usePartsMarkup';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Plus, X } from 'lucide-react';
import { useCompanyLocale } from '@/hooks/useCompanyLocale';
import { effectiveSellPrice } from '@/utils/partsPricing';

interface AddPartFromCatalogProps {
  jobId: string;
}

export function AddPartFromCatalog({ jobId }: AddPartFromCatalogProps) {
  const { data: partsCatalog = [] } = usePartsCatalog();
  const addPart = useAddJobPart();
  const markupPct = usePartsMarkup();
  const defaultSource = useDefaultPartSource();
  const { formatMoney } = useCompanyLocale();
  const [selectedPartId, setSelectedPartId] = useState<string>('');
  const [sellPrice, setSellPrice] = useState<string>('');
  const [quantity, setQuantity] = useState<string>('1');
  const [notes, setNotes] = useState<string>('');
  const [partSource, setPartSource] = useState<PartSource>(defaultSource);

  const selectedPart = partsCatalog.find(p => p.id === selectedPartId);
  const qty = parseInt(quantity) || 1;
  const sell = parseFloat(sellPrice) || 0;

  // Sell price defaults from the catalog: fixed default_sell_price, else cost + markup.
  const handleSelect = (id: string) => {
    setSelectedPartId(id);
    const part = partsCatalog.find(p => p.id === id);
    if (part) {
      setSellPrice(effectiveSellPrice(part.default_cost, part.default_sell_price, markupPct).toFixed(2));
    }
  };

  const handleAdd = () => {
    if (!selectedPart) return;

    addPart.mutate(
      {
        job_id: jobId,
        part_name: selectedPart.name,
        part_cost: selectedPart.default_cost || 0,
        sell_price: sell,
        quantity: qty,
        notes: notes || undefined,
        part_source: partSource,
      },
      {
        onSuccess: () => {
          setSelectedPartId('');
          setSellPrice('');
          setNotes('');
          setPartSource(defaultSource);
        },
      }
    );
  };

  const handleReset = () => {
    setSelectedPartId('');
    setSellPrice('');
    setQuantity('1');
    setNotes('');
    setPartSource(defaultSource);
  };

  return (
    <div className="space-y-4 p-4 border rounded-lg bg-muted/30">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="space-y-2">
          <Label htmlFor="part-select">Select Part</Label>
          <Select value={selectedPartId} onValueChange={handleSelect}>
            <SelectTrigger id="part-select">
              <SelectValue placeholder="Choose a part..." />
            </SelectTrigger>
            <SelectContent className="bg-popover z-50">
              {partsCatalog.map((part) => (
                <SelectItem key={part.id} value={part.id}>
                  {part.name} - {formatMoney(effectiveSellPrice(part.default_cost, part.default_sell_price, markupPct))}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="part-cost">Internal cost</Label>
          <Input
            id="part-cost"
            type="text"
            value={selectedPart ? formatMoney(selectedPart.default_cost || 0) : '-'}
            disabled
            className="bg-muted"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="part-sell">Sell price</Label>
          <Input
            id="part-sell"
            type="number"
            step="0.01"
            min="0"
            value={sellPrice}
            onChange={(e) => setSellPrice(e.target.value)}
            disabled={!selectedPart}
            placeholder="0.00"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="quantity">Quantity</Label>
          <Input
            id="quantity"
            type="number"
            min="1"
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="total">Customer total</Label>
          <Input
            id="total"
            type="text"
            value={selectedPart ? formatMoney(sell * qty) : '-'}
            disabled
            className="bg-muted font-semibold"
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="part-notes">Notes (optional)</Label>
        <Input
          id="part-notes"
          type="text"
          placeholder="Add any notes about this part..."
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
      </div>

      <div className="space-y-3 p-3 bg-background/50 rounded-lg border">
        <Label>Who paid for this part?</Label>
        <RadioGroup value={partSource} onValueChange={(value: 'company_stock' | 'tech_personal') => setPartSource(value)}>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="company_stock" id="catalog_company_stock" />
            <Label htmlFor="catalog_company_stock" className="font-normal cursor-pointer">
              📦 Company Stock
            </Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="tech_personal" id="catalog_tech_personal" />
            <Label htmlFor="catalog_tech_personal" className="font-normal cursor-pointer">
              🔧 Tech Personal (requires reimbursement)
            </Label>
          </div>
        </RadioGroup>
      </div>

      <div className="flex gap-2">
        <Button
          type="button"
          onClick={handleAdd}
          disabled={!selectedPartId || addPart.isPending}
          size="sm"
          className="gap-2"
        >
          <Plus className="h-4 w-4" />
          {addPart.isPending ? 'Adding...' : 'Add Part'}
        </Button>
        <Button
          type="button"
          onClick={handleReset}
          variant="outline"
          size="sm"
          className="gap-2"
        >
          <X className="h-4 w-4" />
          Clear
        </Button>
      </div>
    </div>
  );
}
